import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Flag } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchUserReports, type AdminReport } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A3.17 — Shikoyatlar: foydalanuvchidan VA unga qarshi. GET
 * /admin/users/:id/reports (backend `bbb3fa7`). reporter_id yoki
 * target(user)=:id bo'yicha. Foydalanuvchi kartasidan kirish.
 */
export function UserReports() {
  const t = useT()
  const { id = '' } = useParams()
  const uid = Number(id)

  const columns = useMemo<AdminColumn<AdminReport>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      {
        // undan / unga — joriy user reporter'mi yoki target'mi
        key: 'reporter_id',
        header: t.userExtra.direction,
        type: 'text',
        width: 90,
        sortable: false,
        render: (r) =>
          r.reporter_id === uid ? (
            <span className="text-warning">{t.userExtra.by}</span>
          ) : (
            <span className="text-danger">{t.userExtra.against}</span>
          ),
      },
      { key: 'target_type', header: t.userExtra.target, type: 'text', width: 100 },
      { key: 'category', header: t.mod.category, type: 'text', width: 120 },
      { key: 'reason', header: t.common.reason, type: 'text', minWidth: 200 },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'pending', label: t.mod.pending, status: 'pending' },
          { value: 'resolved', label: t.userExtra.resolved, status: 'active' },
          { value: 'dismissed', label: t.userExtra.dismissed, status: 'deleted' },
        ],
      },
      {
        key: 'id',
        header: t.common.actions,
        type: 'text',
        width: 90,
        sortable: false,
        render: (r) => (
          <Link to={`/moderation/${r.id}`} className="text-accent hover:underline">
            {t.userExtra.openReport}
          </Link>
        ),
      },
      { key: 'created_at', header: t.col.time, type: 'date', width: 160 },
    ],
    [t, uid],
  )

  const fetcher = useCallback((params: FetchParams) => fetchUserReports(uid, params), [uid])

  return (
    <>
      <PageHeader
        title={t.userExtra.reportsTitle}
        screenId="A3.17"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminReport>
        screenId="A3.17"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <Flag size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
